import { forwardRef, useId, useImperativeHandle, useRef, useState, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { Private } from '@shared/components/common/Private';
import { cn } from '@shared/utils/cn';
import { generateRandomDocumentId } from '@shared/utils/randomDocument';
import { useMercadoPagoSDK } from '@features/booking/hooks/useMercadoPagoSDK';
import { CardInputError, formatCardNumber, formatExpiry } from '@features/booking/utils/cardInput';
import { isAmexDepositBlockedForMercadoPago } from '@features/booking/utils/amexChange';
import { DEPOSIT_GUARANTEE } from '@features/booking/state/types';

export interface MercadoPagoCardHandle {
  /**
   * The MercadoPago token of the card entered, for a deposit, the payment_method_id MercadoPago resolves from its
   * number, and the identity document the token was minted with; rejects with a CardInputError for a card to correct
   * (a number MercadoPago names no payment method for included), and with the SDK's error when either call fails.
   */
  tokenize: () => Promise<{ tokenMP: string; paymentMethod: string; identityDocument: string }>;
}

type Card = { holderName: string; cardNumber: string; expiry: string; cvc: string };

const INPUT_CLASS = 'w-full border border-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand';

/** The card inputs MercadoPago tokenizes from: plain inputs, not iframes, so session replay never records them (<Private>). */
const MercadoPagoCardFields = forwardRef<MercadoPagoCardHandle, { allowAMEXBR?: boolean; className?: string }>(
  ({ allowAMEXBR, className }, ref) => {
    const { t } = useTranslation('booking');
    const mpSDK = useMercadoPagoSDK();
    const [card, setCard] = useState<Card>({ holderName: '', cardNumber: '', expiry: '', cvc: '' });
    // MercadoPago requires an identity document; the other MercadoPago forms send a random one too.
    const docNumber = useRef(String(generateRandomDocumentId()));
    const idPrefix = useId();

    useImperativeHandle(ref, () => ({
      tokenize: async () => {
        const digits = card.cardNumber.replace(/\D/g, '');
        const [month, year] = card.expiry.split('/');
        const checks: [failing: boolean, key: string][] = [
          [!mpSDK.isReady, mpSDK.error ? 'PAYMENT_SDK_LOAD_ERROR' : 'PAYMENT_LOADING'],
          [!card.holderName.trim(), 'FORM_ERROR_CREDIT_CARD_NAME'],
          [digits.length < 13 || digits.length > 19, 'FORM_ERROR_CREDIT_CARD'],
          [!/^(0[1-9]|1[0-2])\/\d{2}$/.test(card.expiry), 'FORM_ERROR_CREDIT_CARD_EXPIRES'],
          [card.cvc.length < 3, 'FORM_ERROR_SECURITY_CODE'],
          [isAmexDepositBlockedForMercadoPago({ isAmex: /^3[47]/.test(digits), allowAMEXBR, guaranteeType: DEPOSIT_GUARANTEE }), 'STRIPE_BRAZIL_AMEX_ERROR'],
        ];
        const problem = checks.find(([failing]) => failing);
        if (problem) throw new CardInputError(problem[1]);
        // Resolved at submit, from the number as sent: the booking form's per-keystroke lookup can lag a fast guest.
        const paymentMethod = await mpSDK.getPaymentMethod(digits.slice(0, 6)).catch((err: unknown) => {
          // useMercadoPagoSDK's answer for a number with no payment method: the guest corrects it.
          throw err instanceof Error && err.message === 'Failed to detect payment method' ? new CardInputError('FORM_ERROR_CREDIT_CARD') : err;
        });
        const tokenMP = await mpSDK.createToken({
          holderName: card.holderName,
          cardNumber: digits,
          securityCode: card.cvc,
          expirationMonth: month,
          expirationYear: year,
          docNumber: docNumber.current,
        });
        return { tokenMP, paymentMethod, identityDocument: docNumber.current };
      },
    }), [card, mpSDK, allowAMEXBR]);

    const field = (key: keyof Card, label: string, format: (value: string) => string, attrs: InputHTMLAttributes<HTMLInputElement>) => (
      <div className="flex-1">
        <label htmlFor={`${idPrefix}-${key}`} className="block text-sm font-medium text-foreground/70 mb-1">{t(label)}</label>
        <input
          id={`${idPrefix}-${key}`}
          type="text"
          className={INPUT_CLASS}
          value={card[key]}
          onChange={(e) => {
            const value = format(e.target.value);
            setCard((current) => ({ ...current, [key]: value }));
          }}
          {...attrs}
        />
      </div>
    );

    return (
      <Private className={cn('w-full space-y-3', className)}>
        {mpSDK.isReady ? (
          <>
            {field('holderName', 'PAYMENT_LABEL_CC_2', (value) => value, { autoComplete: 'cc-name' })}
            {field('cardNumber', 'PAYMENT_LABEL_CC', formatCardNumber, { autoComplete: 'cc-number', inputMode: 'numeric', maxLength: 23 })}
            <div className="flex gap-3">
              {field('expiry', 'STRING_PAYMENT_EXPIRES', formatExpiry, { autoComplete: 'cc-exp', inputMode: 'numeric', maxLength: 5 })}
              {field('cvc', 'STRING_SECURITY_CODE', (value) => value.replace(/\D/g, '').slice(0, 4), { autoComplete: 'cc-csc', inputMode: 'numeric', maxLength: 4 })}
            </div>
          </>
        ) : (
          <p className="text-sm text-foreground/50 text-center py-2">{t(mpSDK.error ? 'PAYMENT_SDK_LOAD_ERROR' : 'PAYMENT_LOADING')}</p>
        )}
      </Private>
    );
  },
);
MercadoPagoCardFields.displayName = 'MercadoPagoCardFields';

export default MercadoPagoCardFields;
